import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, X, Check, Loader2, SwitchCamera } from "lucide-react";
import { toast } from "sonner";

const FRIENDLY_ERRORS = {
  NotAllowedError: "Accesso alla fotocamera negato. Apri le impostazioni del browser e abilita la camera per questo sito.",
  NotFoundError: "Nessuna fotocamera trovata sul dispositivo.",
  NotReadableError: "La fotocamera è in uso da un'altra app. Chiudi le altre app e riprova.",
  OverconstrainedError: "Nessuna fotocamera adatta. Provo con la frontale...",
  SecurityError: "Permesso negato per motivi di sicurezza. Usa HTTPS o sblocca la camera.",
};

export default function BarcodeScanner({ open, onOpenChange, onDetected }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null); // ZXing returns IScannerControls with .stop()
  const streamRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [facing, setFacing] = useState("environment"); // 'environment' | 'user'

  const stopAll = () => {
    try { controlsRef.current?.stop?.(); } catch (_e) { /* noop */ }
    try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
    controlsRef.current = null;
    streamRef.current = null;
  };

  useEffect(() => {
    if (!open) {
      stopAll();
      return;
    }
    setScanning(true);
    setError(null);
    setLastResult(null);

    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw Object.assign(new Error("Camera API non disponibile"), { name: "NotFoundError" });
        }

        // 1) Open the camera with the desired facing mode FIRST so we get a live preview ASAP
        const constraints = {
          audio: false,
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        };
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (e) {
          // Fallback: try without facingMode constraint (some Android Chrome instances are picky)
          if (e?.name === "OverconstrainedError" || e?.name === "NotFoundError") {
            stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          } else {
            throw e;
          }
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.setAttribute("muted", "true");
        try { await video.play(); } catch (_e) { /* iOS may need user gesture but Dialog opens via gesture */ }

        // 2) Hand off the same stream to ZXing for continuous decoding
        const reader = new BrowserMultiFormatReader();
        readerRef.current = reader;
        controlsRef.current = await reader.decodeFromStream(stream, video, (result, err) => {
          if (result) {
            const text = result.getText();
            setLastResult(text);
            setScanning(false);
            stopAll();
            onDetected?.(text);
          }
          // err typed NotFoundException is normal between frames → ignore
        });
      } catch (e) {
        const name = e?.name || "";
        const friendly = FRIENDLY_ERRORS[name] || e?.message || "Errore fotocamera";
        setError(friendly);
        setScanning(false);
      }
    };

    start();

    return () => {
      stopAll();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facing]);

  const handleClose = (v) => {
    stopAll();
    onOpenChange(v);
  };

  const toggleFacing = () => {
    setFacing((f) => (f === "environment" ? "user" : "environment"));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="barcode-scanner-dialog" className="max-w-md bg-white">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#E8F3EC] text-[#2E4A3B] flex items-center justify-center flex-shrink-0">
              <Camera size={20} />
            </div>
            <div>
              <DialogTitle style={{ fontFamily: "Work Sans, sans-serif" }}>Scansiona codice a barre</DialogTitle>
              <DialogDescription>Inquadra il codice EAN/UPC del prodotto</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-black border border-[#EAE7E1]">
            <video
              ref={videoRef}
              data-testid="barcode-video"
              className="w-full h-full object-cover"
              playsInline
              autoPlay
              muted
            />
            {/* Scanning overlay */}
            {scanning && !lastResult && !error && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="border-2 border-[#E8F3EC] w-3/4 h-1/3 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-md flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" />
                  Inquadra il codice...
                </div>
              </div>
            )}
            {lastResult && (
              <div className="absolute inset-0 bg-[#2E4A3B]/85 flex items-center justify-center">
                <div className="text-center text-white space-y-2">
                  <Check size={36} className="mx-auto" />
                  <div className="font-mono text-sm" data-testid="barcode-result">{lastResult}</div>
                </div>
              </div>
            )}
            {error && (
              <div className="absolute inset-0 bg-[#FCEAE8] flex flex-col items-center justify-center text-center px-6">
                <X size={36} className="text-[#8E3B2A] mb-2" />
                <div className="text-sm font-medium text-[#8E3B2A]">{error}</div>
              </div>
            )}
            {/* Camera flip button (mobile) */}
            {!error && !lastResult && (
              <button
                type="button"
                data-testid="barcode-flip-camera-btn"
                onClick={toggleFacing}
                className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2"
                title="Cambia fotocamera"
              >
                <SwitchCamera size={16} />
              </button>
            )}
          </div>

          <div className="text-xs text-[#8E918C] text-center">
            Funziona con EAN-13, EAN-8, UPC, Code 128, QR e altri formati standard.
            {!error && " Usa il bottone in alto a destra per cambiare fotocamera (frontale/posteriore)."}
          </div>

          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => handleClose(false)}
              data-testid="barcode-close-btn"
              className="border-[#EAE7E1]"
            >
              Chiudi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
